import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Check, Leaf, Droplets, Mountain } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = '/api';

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addedToCart, setAddedToCart] = useState(false);
  const { addToCart, loading: cartLoading } = useCart();

  useEffect(() => {
    document.title = 'Shop - Salt & Stone';
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleAddToCart = async (productId) => {
    await addToCart(productId);
    setAddedToCart(true);
    toast.success('Added to cart');
    setTimeout(() => setAddedToCart(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-24">
        <div className="spinner"></div>
      </div>
    );
  }

  const product = products[0]; // We have one main product

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="shop-page">
      {/* Hero */}
      <section className="py-16 md:py-24 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <span className="caption block mb-6 animate-fade-in opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            The Collection
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#F2F0E9] mb-8 animate-fade-in opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            Shop
          </h1>
          <div className="w-20 h-px bg-[#D4AF37] mx-auto animate-fade-in opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}></div>
        </div>
      </section>

      {/* Product Display */}
      {product && (
        <section className="py-12 md:py-24 px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Product Image */}
              <div className="lg:sticky lg:top-32 relative">
                {/* Sale Badge */}
                {product.on_sale && (
                  <div className="absolute top-6 left-6 z-10">
                    <div className="bg-[#D4AF37] text-[#050505] px-4 py-2">
                      <span className="text-xs font-medium tracking-widest uppercase">Summer Sale</span>
                    </div>
                  </div>
                )}
                <div className="aspect-[4/5] bg-[#0A0A0A] overflow-hidden group flex items-center justify-center">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover object-center transition-transform duration-1000 group-hover:scale-105"
                    style={{ objectPosition: '50% 35%' }}
                    data-testid="product-image"
                  />
                </div>
              </div>

              {/* Product Details */}
              <div>
                <span className="caption block mb-4">Salt & Stone</span>
                <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9] mb-4" data-testid="product-name">
                  {product.name}
                </h2>
                
                {/* Specs */}
                <div className="flex flex-wrap gap-4 mb-8">
                  <span className="badge-gold">{product.volume}</span>
                  <span className="badge-gold">{product.alcohol_content}</span>
                  <span className="badge-gold">{product.botanicals_count} Botanicals</span>
                </div>

                {/* Price with Sale */}
                <div className="mb-8">
                  {product.on_sale && product.original_price ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        <span className="font-['Playfair_Display'] text-5xl text-[#D4AF37]" data-testid="product-price">
                          €{product.price.toFixed(0)}
                        </span>
                        <span className="text-[#F2F0E9]/40 text-2xl line-through">
                          €{product.original_price.toFixed(0)}
                        </span>
                      </div>
                      <p className="text-[#D4AF37]/80 text-sm italic">
                        Summer Solstice — Celebrate the first light of the season
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="font-['Playfair_Display'] text-5xl text-[#F2F0E9]" data-testid="product-price">
                        €{product.price.toFixed(0)}
                      </span>
                      <span className="text-[#F2F0E9]/50">.00</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="mb-10">
                  <p className="text-[#F2F0E9]/70 leading-relaxed mb-6">
                    {product.description}
                  </p>
                </div>

                {/* Add to Cart */}
                <div className="mb-12">
                  <button
                    onClick={() => handleAddToCart(product.id)}
                    disabled={cartLoading || !product.in_stock}
                    className={`btn-primary w-full md:w-auto flex items-center justify-center gap-3 ${
                      addedToCart ? 'bg-green-600 hover:bg-green-600' : ''
                    }`}
                    data-testid="add-to-cart-btn"
                  >
                    {addedToCart ? (
                      <>
                        <Check className="w-5 h-5" />
                        Added to Cart
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-5 h-5" />
                        Add to Cart
                      </>
                    )}
                  </button>
                  
                  {addedToCart && (
                    <Link 
                      to="/cart" 
                      className="block mt-4 text-center text-[#D4AF37] text-sm hover:underline"
                    >
                      View Cart
                    </Link>
                  )}
                </div>

                {/* Divider */}
                <div className="w-full h-px bg-white/10 mb-10"></div>

                {/* Features */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <Leaf className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-[#F2F0E9] text-sm font-medium mb-1">30 Botanicals</h4>
                      <p className="text-[#F2F0E9]/50 text-xs">Hand-selected from Cyprus</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Droplets className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-[#F2F0E9] text-sm font-medium mb-1">Unfiltered</h4>
                      <p className="text-[#F2F0E9]/50 text-xs">Rich in botanical oils</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mountain className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-[#F2F0E9] text-sm font-medium mb-1">Small Batch</h4>
                      <p className="text-[#F2F0E9]/50 text-xs">Crafted with care</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Tasting Notes */}
      <section className="py-24 md:py-32 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <span className="caption block mb-4">Tasting Notes</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9]">
              The Experience
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <h3 className="font-['Playfair_Display'] text-xl text-[#D4AF37] mb-4">Nose</h3>
              <p className="text-[#F2F0E9]/60 text-sm leading-relaxed">
                An aromatic bouquet of wild Mediterranean herbs leads, followed by warm honey and delicate anise. Fresh citrus zest lifts the profile with brightness.
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-['Playfair_Display'] text-xl text-[#D4AF37] mb-4">Palate</h3>
              <p className="text-[#F2F0E9]/60 text-sm leading-relaxed">
                Bold herbal notes dominate with rosemary and thyme. Sweet honey and subtle anise create depth, while citrus adds vibrancy. Gentle spices and soft floral whispers emerge.
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-['Playfair_Display'] text-xl text-[#D4AF37] mb-4">Finish</h3>
              <p className="text-[#F2F0E9]/60 text-sm leading-relaxed">
                Long and warming with lingering herbal complexity. Honey sweetness carries through, softened by citrus, leaving a smooth and gracefully sweet close.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Info */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="container mx-auto max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <h4 className="text-[#F2F0E9] text-sm font-medium mb-2">Free Shipping Cyprus</h4>
              <p className="text-[#F2F0E9]/50 text-xs">Complimentary delivery</p>
            </div>
            <div>
              <h4 className="text-[#F2F0E9] text-sm font-medium mb-2">Secure Checkout</h4>
              <p className="text-[#F2F0E9]/50 text-xs">SSL encrypted payment</p>
            </div>
            <div>
              <h4 className="text-[#F2F0E9] text-sm font-medium mb-2">Gift Packaging</h4>
              <p className="text-[#F2F0E9]/50 text-xs">Available on request</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
